<template>
  <v-container fluid>
    <section class="ps-product-detail">
      <!-- ==================== LOADING ==================== -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" />
        <p class="mt-3 text-caption">{{ $t("productDetail.loading") }}</p>
      </div>

      <!-- ==================== NOT FOUND ==================== -->
      <v-card
        v-else-if="notFound"
        rounded="lg"
        outlined
        class="text-center py-12"
      >
        <v-icon size="48" color="error">fal fa-exclamation-triangle</v-icon>
        <p class="mt-3 font-weight-black">
          {{ $t("productDetail.notFound", { id: pid }) }}
        </p>
        <v-btn depressed class="ps-pd-btn mt-3" @click="goBack">
          {{ $t("productDetail.backShort") }}
        </v-btn>
      </v-card>

      <!-- ==================== CONTENT ==================== -->
      <template v-else-if="product">
        <div class="d-flex align-start flex-wrap ps-pd-header">
          <div class="ps-pd-title-block">
            <div class="ps-pd-idline">
              <span>{{ $t("productDetail.thId") }} {{ product.id }}</span>
              <span>·</span>
              <span>{{ product.code }}</span>
            </div>
            <h1 class="ps-pd-title">{{ localName }}</h1>
            <div class="d-flex flex-wrap ps-pd-badges">
              <span
                v-for="b in badges"
                :key="b.label"
                class="ps-badge"
                :class="'ps-badge--' + b.tone"
              >
                {{ b.label }}
              </span>
            </div>
          </div>
          <v-spacer />
          <div class="d-flex align-center ps-pd-actions">
            <v-btn
              depressed
              outlined
              class="ps-pd-btn"
              @click="showBarcodeModal = true"
            >
              <v-icon x-small left>fal fa-barcode</v-icon>
              {{ $t("productDetail.printBarcode") }}
            </v-btn>
            <v-btn depressed outlined class="ps-pd-btn" @click="requestDelete">
              <v-icon x-small left>fal fa-trash</v-icon>
              {{ $t("productDetail.delete") }}
            </v-btn>
            <v-btn
              depressed
              outlined
              class="ps-pd-btn ps-pd-btn--accent"
              :to="`/manage-edit-product/${product.id}`"
            >
              <v-icon x-small left>fal fa-pencil</v-icon>
              {{ $t("productDetail.editProduct") }}
            </v-btn>
          </div>
        </div>

        <div class="ps-pd-stats">
          <div v-for="s in statCards" :key="s.label" class="ps-stat-card">
            <div class="d-flex align-center">
              <span class="ps-stat-label">{{ s.label }}</span>
              <v-spacer />
              <span class="ps-stat-tag" :class="'ps-stat-tag--' + s.tone">{{
                s.tag
              }}</span>
            </div>
            <div class="d-flex align-baseline ps-stat-value-row">
              <span
                class="ps-stat-value"
                :class="{ 'ps-stat-value--accent': s.accent }"
                >{{ s.value }}</span
              >
              <span v-if="s.unit" class="ps-stat-unit">{{ s.unit }}</span>
            </div>
            <span class="ps-stat-note">{{ s.note }}</span>
          </div>
        </div>

        <div class="ps-pd-grid">
          <div class="ps-pd-main">
            <!-- Identity -->
            <section class="ps-card">
              <div class="ps-section-head">
                <span class="ps-tick"></span>
                <h2>{{ $t("productDetail.secIdentity") }}</h2>
                <span class="ps-section-note">{{ langNote }}</span>
              </div>
              <div class="ps-identity-grid">
                <div class="ps-identity-media">
                  <div class="ps-media-box">
                    <v-img
                      v-if="mainImage"
                      :src="mainImage"
                      height="168"
                      width="168"
                      contain
                    />
                    <template v-else>
                      <v-icon size="30" color="rgba(148,163,184,0.5)"
                        >fal fa-box</v-icon
                      >
                    </template>
                    <span class="ps-media-caption">{{
                      product.img || $t("productDetail.notSet")
                    }}</span>
                  </div>
                  <div class="d-flex ps-gallery-badges">
                    <span
                      v-for="g in galleryBadges"
                      :key="g.label"
                      class="ps-gallery-badge"
                      :class="{ 'is-on': g.on }"
                      >{{ g.label }}</span
                    >
                  </div>
                  <span class="ps-media-note">{{
                    $t("productDetail.primaryImageOnly")
                  }}</span>
                </div>

                <div class="ps-identity-fields">
                  <div class="ps-names-block">
                    <span class="ps-field-caption">{{
                      $t("productDetail.namesLabel")
                    }}</span>
                    <div v-for="n in nameRows" :key="n.lang" class="ps-name-row">
                      <span
                        class="ps-name-dot"
                        :class="{ 'is-on': n.filled }"
                      ></span>
                      <span class="ps-name-lang">{{ n.lang }}</span>
                      <span
                        class="ps-name-value"
                        :class="{ 'is-muted': !n.filled }"
                        >{{ n.value }}</span
                      >
                    </div>
                  </div>

                  <div class="ps-fields-grid">
                    <div
                      v-for="f in identityFields"
                      :key="f.label"
                      class="ps-field"
                    >
                      <span class="ps-field-label">{{ f.label }}</span>
                      <span class="ps-field-value">{{ f.value }}</span>
                      <span v-if="f.note" class="ps-field-note">{{
                        f.note
                      }}</span>
                    </div>
                  </div>

                  <div class="ps-desc-block">
                    <span class="ps-field-caption">{{
                      $t("productDetail.desc")
                    }}</span>
                    <span class="ps-desc-text" :class="{ 'is-empty': !hasDesc }">{{
                      descText
                    }}</span>
                  </div>
                </div>
              </div>
            </section>

            <!-- Price -->
            <section class="ps-card">
              <div class="ps-section-head">
                <span class="ps-tick"></span>
                <h2>{{ $t("productDetail.secPrice") }}</h2>
                <span class="ps-section-note">{{ currencyNote }}</span>
              </div>
              <div class="ps-price-grid">
                <div
                  v-for="p in priceRows"
                  :key="p.label"
                  class="ps-price-card"
                  :class="{ 'is-tint': p.tint }"
                >
                  <span class="ps-price-label">{{ p.label }}</span>
                  <div class="d-flex align-baseline ps-price-value-row">
                    <span
                      class="ps-price-value"
                      :class="{ 'is-tint': p.tint }"
                      >{{ p.value }}</span
                    >
                    <span v-if="p.unit" class="ps-price-unit">{{
                      p.unit
                    }}</span>
                  </div>
                  <span class="ps-price-note">{{ p.note }}</span>
                </div>
              </div>
              <div class="ps-meta-grid ps-meta-grid--3">
                <div v-for="f in priceMeta" :key="f.label" class="ps-field">
                  <span class="ps-field-label">{{ f.label }}</span>
                  <span class="ps-field-value">{{ f.value }}</span>
                  <span v-if="f.note" class="ps-field-note">{{ f.note }}</span>
                </div>
              </div>
            </section>

            <!-- Inventory -->
            <section v-if="product.track_stock" class="ps-card">
              <div class="ps-section-head">
                <span class="ps-tick"></span>
                <h2>{{ $t("productDetail.secStock") }}</h2>
                <span
                  class="ps-stat-tag"
                  :class="'ps-stat-tag--' + (lowStock ? 'on' : 'off')"
                  >{{
                    lowStock
                      ? $t("productDetail.stockAtNotify")
                      : $t("productDetail.ready")
                  }}</span
                >
              </div>
              <div class="d-flex align-baseline ps-stock-value-row">
                <span class="ps-stock-value">{{ fmtNum(product.stock) }}</span>
                <span class="ps-stock-unit">{{
                  product.size ? product.size.unit : ""
                }}</span>
                <v-spacer />
                <span class="ps-stock-notify-note">{{ notifyNote }}</span>
              </div>
              <div class="ps-stock-bar">
                <span
                  class="ps-stock-bar__fill"
                  :class="{ 'is-warn': lowStock }"
                  :style="{ width: stockBarPercent + '%' }"
                ></span>
              </div>
              <div class="ps-meta-grid ps-meta-grid--4">
                <div v-for="f in stockMeta" :key="f.label" class="ps-field">
                  <span class="ps-field-label">{{ f.label }}</span>
                  <span class="ps-field-value">{{ f.value }}</span>
                  <span v-if="f.note" class="ps-field-note">{{ f.note }}</span>
                </div>
              </div>
            </section>

            <!-- Sales history -->
            <section class="ps-card">
              <div class="ps-section-head">
                <span class="ps-tick"></span>
                <h2>{{ $t("productDetail.history") }}</h2>
                <span class="ps-section-note"
                  >{{ $t("productDetail.since") }} {{ createdShort }}</span
                >
              </div>
              <div class="ps-history-grid">
                <div
                  v-for="h in historyCards"
                  :key="h.label"
                  class="ps-history-card"
                >
                  <span class="ps-history-label">{{ h.label }}</span>
                  <div class="d-flex align-baseline ps-history-value-row">
                    <span class="ps-history-value">{{ h.value }}</span>
                    <span v-if="h.unit" class="ps-history-unit">{{
                      h.unit
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="d-flex align-center ps-history-note">
                <v-icon x-small class="mr-2">fal fa-info-circle</v-icon>
                <span>{{
                  product.history_sum_qty
                    ? $t("productDetail.totalsAllSales")
                    : $t("productDetail.noSalesYet")
                }}</span>
              </div>
            </section>

            <!-- variants -->
            <section
              v-if="product.variants && product.variants.length"
              class="ps-card"
            >
              <div class="ps-section-head">
                <span class="ps-tick"></span>
                <h2>
                  {{
                    $t("productDetail.variantsTitle", {
                      n: product.variants.length,
                    })
                  }}
                </h2>
              </div>
              <div class="ps-table-wrap">
                <table class="ps-table">
                  <thead>
                    <tr>
                      <th>{{ $t("productDetail.thId") }}</th>
                      <th>{{ $t("productDetail.thSku") }}</th>
                      <th>{{ $t("productDetail.thOptions") }}</th>
                      <th class="ps-th--right">
                        {{ $t("productDetail.thPrice") }}
                      </th>
                      <th class="ps-th--right">
                        {{ $t("productDetail.thCost") }}
                      </th>
                      <th>{{ $t("productDetail.thBarcode") }}</th>
                      <th class="ps-th--right">
                        {{ $t("productDetail.thStock") }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="v in product.variants" :key="v.id">
                      <td class="ps-td-strong">{{ v.id }}</td>
                      <td>{{ v.sku || "—" }}</td>
                      <td>
                        <span
                          v-for="(val, key) in v.options"
                          :key="key"
                          class="ps-mini-chip"
                          >{{ key }}: {{ val }}</span
                        >
                        <span
                          v-if="!Object.keys(v.options || {}).length"
                          class="ps-field-note"
                          >—</span
                        >
                      </td>
                      <td class="ps-td--right ps-td-strong">
                        {{ fmt(v.price) }}
                      </td>
                      <td class="ps-td--right">{{ fmt(v.cost) }}</td>
                      <td class="ps-td-mono">{{ v.barcode || "—" }}</td>
                      <td
                        class="ps-td--right ps-td-strong"
                        :class="{ 'is-danger': v.stock <= 0 }"
                      >
                        {{ fmtNum(v.stock) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <!-- options -->
            <section
              v-if="product.options && product.options.length"
              class="ps-card"
            >
              <div class="ps-section-head">
                <span class="ps-tick"></span>
                <h2>{{ $t("productDetail.optionsTitle") }}</h2>
              </div>
              <div
                v-for="o in product.options"
                :key="o.name"
                class="d-flex align-center flex-wrap mb-2"
              >
                <span class="ps-field-label mr-3">{{ o.name }}</span>
                <span v-for="val in o.values" :key="val" class="ps-mini-chip">{{
                  val
                }}</span>
              </div>
            </section>

            <!-- bundle items -->
            <section
              v-if="product.bundle_items && product.bundle_items.length"
              class="ps-card"
            >
              <div class="ps-section-head">
                <span class="ps-tick"></span>
                <h2>
                  {{
                    $t("productDetail.bundleTitle", {
                      n: product.bundle_items.length,
                    })
                  }}
                </h2>
              </div>
              <div class="ps-table-wrap">
                <table class="ps-table">
                  <thead>
                    <tr>
                      <th>{{ $t("productDetail.thProduct") }}</th>
                      <th>{{ $t("productDetail.thCode") }}</th>
                      <th class="ps-th--right">
                        {{ $t("productDetail.thQty") }}
                      </th>
                      <th class="ps-th--right">
                        {{ $t("productDetail.thPrice") }}
                      </th>
                      <th class="ps-th--right">
                        {{ $t("productDetail.thStock") }}
                      </th>
                      <th class="ps-th--right">
                        {{ $t("productDetail.thTrack") }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(bi, i) in product.bundle_items"
                      :key="bi.product_id || i"
                    >
                      <td>
                        <div class="d-flex align-center">
                          <v-avatar size="30" class="mr-2 rounded-lg ps-bundle-avatar">
                            <v-img
                              v-if="bi.product && bi.product.img"
                              :src="IMAGES(bi.product.img)"
                              contain
                            />
                            <v-icon v-else x-small color="rgba(148,163,184,0.5)"
                              >fal fa-box</v-icon
                            >
                          </v-avatar>
                          <span class="ps-td-strong">{{
                            (bi.product && bi.product.name) || "—"
                          }}</span>
                        </div>
                      </td>
                      <td class="ps-td-mono">
                        {{ (bi.product && bi.product.code) || "—" }}
                      </td>
                      <td class="ps-td--right ps-td-strong">
                        {{ bi.quantity }}
                      </td>
                      <td class="ps-td--right">
                        {{ fmt(bi.product && bi.product.price_sale) }}
                      </td>
                      <td
                        class="ps-td--right"
                        :class="{ 'is-danger': bi.product && bi.product.stock <= 0 }"
                      >
                        {{ fmtNum(bi.product && bi.product.stock) }}
                      </td>
                      <td class="ps-td--right">
                        {{ (bi.product && bi.product.track_stock) ?? "—" }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <aside class="ps-pd-side">
            <!-- Status -->
            <section class="ps-card ps-card--side">
              <div class="ps-section-head">
                <span class="ps-tick"></span>
                <h2>{{ $t("productDetail.secStatus") }}</h2>
              </div>
              <div class="ps-toggle-row">
                <v-switch
                  :input-value="product.is_active == 1"
                  :disabled="togglingActive"
                  inset
                  dense
                  hide-details
                  class="ma-0 pa-0 ps-switch"
                  @change="toggleActive"
                />
                <div class="ps-toggle-meta">
                  <span class="ps-toggle-title">{{
                    $t("productDetail.active")
                  }}</span>
                  <span class="ps-toggle-note">{{
                    product.is_active == 1
                      ? $t("productDetail.statusActiveOnNote")
                      : $t("productDetail.statusActiveOffNote")
                  }}</span>
                </div>
              </div>
              <div class="ps-toggle-row">
                <v-switch
                  :input-value="product.is_online == 1"
                  disabled
                  inset
                  dense
                  hide-details
                  class="ma-0 pa-0 ps-switch"
                />
                <div class="ps-toggle-meta">
                  <span class="ps-toggle-title">{{
                    $t("productDetail.sellOnlineLabel")
                  }}</span>
                  <span class="ps-toggle-note">{{
                    product.is_online == 1
                      ? $t("productDetail.statusOnlineOnNote")
                      : $t("productDetail.statusOnlineOffNote")
                  }}</span>
                  <span class="ps-toggle-note ps-toggle-note--italic">{{
                    $t("productDetail.editToChangeNote")
                  }}</span>
                </div>
              </div>
            </section>

            <!-- Classification -->
            <section class="ps-card ps-card--side">
              <div class="ps-section-head">
                <span class="ps-tick"></span>
                <h2>{{ $t("productDetail.secClassification") }}</h2>
              </div>
              <div
                v-for="c in classificationRows"
                :key="c.label"
                class="ps-side-row"
              >
                <span class="ps-side-label">{{ c.label }}</span>
                <div class="ps-side-value-col">
                  <span class="ps-side-value">{{ c.value }}</span>
                  <span v-if="c.note" class="ps-field-note">{{ c.note }}</span>
                </div>
              </div>
            </section>

            <!-- Capabilities -->
            <section class="ps-card ps-card--side">
              <div class="ps-section-head">
                <span class="ps-tick"></span>
                <h2>{{ $t("productDetail.secCapabilities") }}</h2>
                <span class="ps-section-note">{{ capNote }}</span>
              </div>
              <div v-for="c in capabilities" :key="c.title" class="ps-cap-row">
                <span class="ps-cap-mark" :class="{ 'is-on': c.on }">
                  <v-icon x-small>{{
                    c.on ? "fal fa-check" : "fal fa-times"
                  }}</v-icon>
                </span>
                <div class="ps-toggle-meta">
                  <span
                    class="ps-toggle-title"
                    :class="{ 'is-muted': !c.on }"
                    >{{ c.title }}</span
                  >
                  <span class="ps-toggle-note">{{ c.note }}</span>
                </div>
              </div>
            </section>

            <!-- Record -->
            <section class="ps-card ps-card--side">
              <div class="ps-section-head">
                <span class="ps-tick"></span>
                <h2>{{ $t("productDetail.secRecord") }}</h2>
              </div>
              <div v-for="r in recordRows" :key="r.label" class="ps-side-row ps-side-row--tight">
                <span class="ps-side-label">{{ r.label }}</span>
                <span class="ps-side-value ps-side-value--mono">{{
                  r.value
                }}</span>
              </div>
            </section>
          </aside>
        </div>
      </template>

      <app-loading :overlay="overlay" />
      <barcode-preview-modal
        v-model="showBarcodeModal"
        :product="product || {}"
      />
    </section>
  </v-container>
</template>

<script>
import { myMixin } from "@/mixin/main";
import { confirm, toast, error } from "@/controllers/alert";
import BarcodePreviewModal from "@/components/BarcodePreviewModal";

export default {
  name: "ProductDetailPage",
  mixins: [myMixin],
  components: {
    BarcodePreviewModal,
  },
  data: () => ({
    product: null,
    loading: true,
    notFound: false,
    overlay: false,
    showBarcodeModal: false,
    togglingActive: false,
  }),
  computed: {
    pid() {
      return Number(this.$route.params.id);
    },
    localName() {
      const p = this.product;
      if (!p) return "—";
      return this.getProductName(p) || p.name || "—";
    },
    mainImage() {
      const p = this.product;
      if (!p) return "";
      if (p.img) return this.IMAGES(p.img);
      if (Array.isArray(p.images) && p.images.length)
        return this.IMAGES(p.images[0].image);
      return "";
    },
    lowStock() {
      const p = this.product;
      if (!p || p.track_stock == 0) return false;
      return Number(p.stock) <= Number(p.notify_stock || 0);
    },
    stockBarPercent() {
      const p = this.product;
      if (!p) return 0;
      return Math.max(6, Math.min(100, Number(p.stock) || 0));
    },
    notifyNote() {
      const p = this.product;
      if (!p) return "";
      return this.lowStock
        ? `${this.$t("productDetail.stockAtNotify")} · ${this.fmtNum(
            p.notify_stock
          )}`
        : `${this.$t("productDetail.fNotify")} ${this.fmtNum(p.notify_stock)}`;
    },
    hasDesc() {
      const p = this.product;
      return !!(p && p.desc && p.desc.trim());
    },
    descText() {
      return this.hasDesc
        ? this.product.desc
        : this.$t("productDetail.noDescriptionText");
    },
    badges() {
      const p = this.product;
      if (!p) return [];
      const out = [];
      out.push({
        label:
          p.is_active == 1
            ? this.$t("productDetail.active")
            : this.$t("productDetail.inactive"),
        tone: p.is_active == 1 ? "on" : "off",
      });
      out.push({
        label:
          p.is_online == 1
            ? this.$t("productDetail.online")
            : this.$t("productDetail.offline"),
        tone: p.is_online == 1 ? "on" : "off",
      });
      out.push({
        label: this.lowStock
          ? this.$t("productDetail.stockAtNotify")
          : this.$t("productDetail.ready"),
        tone: this.lowStock ? "on" : "off",
      });
      out.push({ label: this.catName(p.category), tone: "off" });
      if (p.is_bundle == 1)
        out.push({ label: this.$t("productDetail.bundleTag"), tone: "on" });
      if (p.has_variations == 1)
        out.push({
          label: this.$t("productDetail.variationsTag"),
          tone: "on",
        });
      if (p.track_stock == 0)
        out.push({ label: this.$t("productDetail.noTrackTag"), tone: "on" });
      if (p.dynamic_pricing == 1)
        out.push({ label: this.$t("productDetail.dynamicTag"), tone: "on" });
      return out;
    },
    profitPerUnit() {
      const p = this.product;
      if (!p) return 0;
      return (p.price_sale || 0) - (p.price_buy || 0);
    },
    profitMargin() {
      const p = this.product;
      if (!p || !p.price_sale) return 0;
      return Math.round(((p.price_sale - p.price_buy) / p.price_sale) * 100);
    },
    statCards() {
      const p = this.product;
      if (!p) return [];
      const sym = p.currency ? p.currency.symbol : "";
      return [
        {
          label: this.$t("productDetail.priceSale"),
          value: this.fmtNum(p.price_sale),
          unit: sym,
          note: `${this.$t("productDetail.fTax")} ${this.fmtNum(
            p.tax_amount
          )} · ${p.currency ? p.currency.code : ""}`,
          tag: this.$t("productDetail.tagCurrent"),
          tone: "off",
        },
        {
          label: this.$t("productDetail.profitUnit"),
          value: "+" + this.fmtNum(this.profitPerUnit),
          unit: sym,
          note: `${this.profitMargin}% ${this.$t("margin")}`,
          tag: this.$t("productDetail.tagPerUnit"),
          tone: "on",
          accent: true,
        },
        {
          label: this.$t("productDetail.stock"),
          value:
            p.track_stock == 0
              ? this.$t("productDetail.notTracked")
              : this.fmtNum(p.stock),
          unit: p.track_stock == 0 ? "" : p.size ? p.size.unit : "",
          note:
            p.track_stock == 0
              ? ""
              : `${this.$t("productDetail.fNotify")} ${this.fmtNum(
                  p.notify_stock
                )}`,
          tag: this.lowStock
            ? this.$t("productDetail.tagAtLimit")
            : this.$t("productDetail.tagOk"),
          tone: this.lowStock ? "on" : "off",
        },
        {
          label: this.$t("productDetail.sold"),
          value: this.fmtNum(p.history_sum_qty),
          unit: p.size ? p.size.unit : this.$t("productDetail.units"),
          note: p.history_sum_qty ? "" : this.$t("productDetail.noSalesYet"),
          tag: this.$t("productDetail.tagLifetime"),
          tone: "off",
        },
      ];
    },
    langNote() {
      const p = this.product;
      if (!p) return "";
      const filled = [p.name, p.name_en, p.name_th, p.name_cn].filter(
        (n) => n && String(n).trim()
      ).length;
      return this.$t("productDetail.langNote", { n: filled });
    },
    nameRows() {
      const p = this.product;
      if (!p) return [];
      return [
        { lang: this.$t("product_name"), value: p.name },
        { lang: this.$t("name_en"), value: p.name_en },
        { lang: this.$t("name_th"), value: p.name_th },
        { lang: this.$t("name_cn"), value: p.name_cn },
      ].map((r) => ({
        lang: r.lang,
        filled: !!(r.value && String(r.value).trim()),
        value:
          r.value && String(r.value).trim()
            ? r.value
            : this.$t("productDetail.notSet"),
      }));
    },
    galleryBadges() {
      const p = this.product;
      if (!p) return [];
      const imgCount = Array.isArray(p.images) ? p.images.length : 0;
      return [
        {
          label: this.$t("productDetail.extraImages", { n: imgCount }),
          on: imgCount > 0,
        },
        {
          label: p.video_url
            ? this.$t("productDetail.hasVideo")
            : this.$t("productDetail.noVideo"),
          on: !!p.video_url,
        },
      ];
    },
    identityFields() {
      const p = this.product;
      if (!p) return [];
      return [
        { label: this.$t("productDetail.thBarcode"), value: p.code || "—" },
        { label: this.$t("productDetail.fProductId"), value: `#${p.id}` },
        {
          label: this.$t("productDetail.fUnit"),
          value: p.size ? p.size.name : "—",
          note: p.size ? `${p.size.unit} · ${p.size.amount}` : "",
        },
        { label: this.$t("productDetail.fPackNumber"), value: p.number ?? "—" },
      ];
    },
    currencyNote() {
      const p = this.product;
      if (!p || !p.currency) return "";
      return `${p.currency.code} · ${this.$t("productDetail.rateWord")} ${
        p.exchange_rate
      }`;
    },
    priceRows() {
      const p = this.product;
      if (!p) return [];
      const sym = p.currency ? p.currency.symbol : "";
      return [
        {
          label: this.$t("productDetail.fPriceBuy"),
          value: this.fmtNum(p.price_buy),
          unit: sym,
          note: this.$t("productDetail.costNote"),
        },
        {
          label: this.$t("productDetail.fPriceSale"),
          value: this.fmtNum(p.price_sale),
          unit: sym,
          note: this.$t("productDetail.priceNote"),
        },
        {
          label: this.$t("productDetail.profitUnit"),
          value: "+" + this.fmtNum(this.profitPerUnit),
          unit: sym,
          note: `${this.profitMargin}% ${this.$t("margin")}`,
          tint: true,
        },
      ];
    },
    priceMeta() {
      const p = this.product;
      if (!p) return [];
      return [
        {
          label: this.$t("productDetail.fTax"),
          value: `${this.fmtNum(p.tax_amount)} ${
            p.currency ? p.currency.symbol : ""
          }`,
          note: p.tax_amount ? "" : this.$t("productDetail.noTaxNote"),
        },
        {
          label: this.$t("productDetail.fCurrency"),
          value: `${p.currency ? p.currency.code : "—"}${
            p.currency && p.currency.symbol ? ` (${p.currency.symbol})` : ""
          }`,
          note: p.currency_id ? `currency_id ${p.currency_id}` : "",
        },
        {
          label: this.$t("productDetail.fRate"),
          value: p.exchange_rate ?? "—",
          note: this.$t("productDetail.baseCurrencyNote"),
        },
      ];
    },
    stockMeta() {
      const p = this.product;
      if (!p) return [];
      return [
        {
          label: this.$t("productDetail.fStock"),
          value: `${this.fmtNum(p.stock)} ${p.size ? p.size.unit : ""}`,
        },
        {
          label: this.$t("productDetail.fNotify"),
          value: this.fmtNum(p.notify_stock),
          note: this.$t("productDetail.notifyMetaNote"),
        },
        {
          label: this.$t("productDetail.fTrack"),
          value:
            p.track_stock == 1
              ? this.$t("productDetail.track1")
              : this.$t("productDetail.track0"),
          note: this.$t("productDetail.trackMetaNote"),
        },
        {
          label: this.$t("productDetail.fExpired"),
          value: p.expired_date ? this.fmtDate(p.expired_date) : "—",
          note: p.expired_date ? "" : this.$t("productDetail.noExpiryNote"),
        },
      ];
    },
    createdShort() {
      const p = this.product;
      if (!p) return "—";
      return this.fmtDate(p.created_at);
    },
    historyCards() {
      const p = this.product;
      if (!p) return [];
      return [
        {
          label: this.$t("productDetail.hQty"),
          value: this.fmtNum(p.history_sum_qty),
          unit: p.size ? p.size.unit : "",
        },
        {
          label: this.$t("productDetail.hTotal"),
          value: this.fmtNum(p.history_sum_total_price),
          unit: p.currency ? p.currency.symbol : "",
        },
        {
          label: this.$t("productDetail.hDiscount"),
          value: this.fmtNum(p.history_sum_discount),
          unit: p.currency ? p.currency.symbol : "",
        },
      ];
    },
    classificationRows() {
      const p = this.product;
      if (!p) return [];
      const cat = p.category || {};
      return [
        {
          label: this.$t("productDetail.fCategory"),
          value: this.catName(cat),
          note: cat.id ? `${this.$t("id")} ${cat.id}` : "",
        },
        {
          label: this.$t("productDetail.fSize"),
          value: p.size ? p.size.name : "—",
          note: p.size ? `${p.size.unit} · ${p.size.amount}` : "",
        },
        {
          label: this.$t("productDetail.fVendor"),
          value: p.vendor ? p.vendor.name : "—",
          note: p.vendor ? `${this.$t("id")} ${p.vendor.id}` : "",
        },
        {
          label: this.$t("productDetail.fBranch"),
          value: p.branch_id ?? "—",
          note: p.store_id ? `${this.$t("productDetail.storeWord")} ${p.store_id}` : "",
        },
        {
          label: this.$t("productDetail.fQrOrder"),
          value: cat.enable_qr_order
            ? this.$t("enabled")
            : this.$t("disabled"),
          note: this.$t("productDetail.fromCategoryNote"),
        },
        {
          label: this.$t("productDetail.fKitchen"),
          value: cat.is_cooking
            ? this.$t("productDetail.cookingItem")
            : this.$t("productDetail.notCookingItem"),
          note: this.$t("productDetail.fromCategoryNote"),
        },
        {
          label: this.$t("productDetail.fBuffet"),
          value: cat.is_buffet_included
            ? this.$t("productDetail.includedWord")
            : this.$t("productDetail.notIncludedWord"),
          note: this.$t("productDetail.fromCategoryNote"),
        },
      ];
    },
    capNote() {
      const p = this.product;
      if (!p) return "";
      const n = [
        p.has_variations,
        p.is_bundle,
        p.dynamic_pricing,
        p.track_stock,
        p.expired_date ? 1 : 0,
      ].filter(Boolean).length;
      return this.$t("productDetail.capNote", { n });
    },
    capabilities() {
      const p = this.product;
      if (!p) return [];
      return [
        {
          title: this.$t("productDetail.capVariations"),
          on: !!p.has_variations,
          note: p.has_variations
            ? this.$t("productDetail.capVariantsCount", {
                n: (p.variants || []).length,
              })
            : this.$t("disabled"),
        },
        {
          title: this.$t("productDetail.capBundle"),
          on: !!p.is_bundle,
          note: p.is_bundle
            ? this.$t("productDetail.capBundleCount", {
                n: (p.bundle_items || []).length,
              })
            : this.$t("disabled"),
        },
        {
          title: this.$t("productDetail.fDynamic"),
          on: !!p.dynamic_pricing,
          note: p.dynamic_pricing
            ? this.$t("enabled")
            : this.$t("productDetail.noDynamicNote"),
        },
        {
          title: this.$t("productDetail.capStockTracking"),
          on: !!p.track_stock,
          note: p.track_stock
            ? this.$t("productDetail.tracksOnSaleNote")
            : this.$t("disabled"),
        },
        {
          title: this.$t("productDetail.capExpiryTracking"),
          on: !!p.expired_date,
          note: p.expired_date
            ? this.fmtDate(p.expired_date)
            : this.$t("productDetail.noExpirySetNote"),
        },
      ];
    },
    recordRows() {
      const p = this.product;
      if (!p) return [];
      return [
        {
          label: this.$t("productDetail.fCreatedAt"),
          value: this.fmtDate(p.created_at),
        },
        {
          label: this.$t("productDetail.fUpdatedAt"),
          value: this.fmtDate(p.updated_at),
        },
        {
          label: this.$t("productDetail.fCreatedBy"),
          value: p.created_by ? `#${p.created_by}` : "—",
        },
        {
          label: this.$t("productDetail.fDeletedAt"),
          value: p.deleted_at ? this.fmtDate(p.deleted_at) : "—",
        },
        { label: this.$t("productDetail.fRecordId"), value: `#${p.id}` },
      ];
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.notFound = false;
      try {
        const { data } = await this.$http.get(`products/${this.pid}`);
        if (data.status && data.data) {
          this.product = data.data;
        } else {
          this.notFound = true;
        }
      } catch (err) {
        this.notFound = true;
        console.log("product-detail load error", err);
      } finally {
        this.loading = false;
      }
    },
    fmt(n) {
      if (n === null || n === undefined) return "—";
      return this.priceWithCurrency(n, this.product ? this.product.currency : null);
    },
    fmtNum(n) {
      if (n === null || n === undefined) return "—";
      return Number(n || 0).toLocaleString("en-US");
    },
    fmtDate(d) {
      if (!d) return "—";
      const dt = new Date(d);
      return isNaN(dt)
        ? String(d)
        : dt.toLocaleString("lo-LA", {
            dateStyle: "medium",
            timeStyle: "short",
          });
    },
    catName(c) {
      if (!c) return "—";
      const key =
        this.$i18n.locale === "en"
          ? "en_name"
          : this.$i18n.locale === "th"
          ? "th_name"
          : this.$i18n.locale === "cn" || this.$i18n.locale === "ch"
          ? "cn_name"
          : "la_name";
      return c[key] || c.la_name || c.en_name || "—";
    },
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push("/products");
    },
    async toggleActive() {
      if (this.togglingActive || !this.product) return;
      this.togglingActive = true;
      const previous = this.product.is_active;
      try {
        const { data } = await this.$http.put(
          `product_active/${this.product.id}`
        );
        if (data.status) {
          this.product.is_active = previous == 1 ? 0 : 1;
          await toast(this, data.message);
        } else {
          await error(this, data?.message || "error_occurred");
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message || err?.message || "error_occurred";
        await error(this, typeof msg === "string" ? msg : "error_occurred");
      } finally {
        this.togglingActive = false;
      }
    },
    async requestDelete() {
      const { isConfirmed } = await confirm({
        props: this,
        title: "productDetail.deleteConfirmTitle",
        text: this.$t("productDetail.deleteConfirmMsg", {
          name: this.localName,
        }),
        con_txt: "delete",
        can_txt: "cancel",
        color_con: "#ef4444",
      });
      if (isConfirmed) {
        await this.confirmDelete();
      }
    },
    async confirmDelete() {
      if (this.overlay) return;
      this.overlay = true;
      try {
        const { data } = await this.$http.delete(`products/${this.pid}`);
        if (data.status) {
          await toast(this, data.message);
          this.$router.push("/products");
        } else {
          await error(this, data?.message || "error_occurred");
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message || err?.message || "error_occurred";
        await error(this, typeof msg === "string" ? msg : "error_occurred");
      } finally {
        this.overlay = false;
      }
    },
  },
};
</script>

<style scoped>
.ps-product-detail {
  --ps-pd-ink: #141021;
  --ps-pd-muted: #64748b;
  --ps-pd-faint: #94a3b8;
  --ps-pd-accent: #5b39c9;
  --ps-pd-accent-soft: #7c5cea;
  --ps-pd-card-bg: #ffffff;
  --ps-pd-card-border: #e6e8f4;
  --ps-pd-line: #eef0f6;
  --ps-pd-line-strong: #e2e8f0;
  --ps-pd-track: #eef0f6;
  --ps-pd-control-bg: #ffffff;
  --ps-pd-danger: #dc2626;
  --ps-pd-pill-bg: rgba(91, 57, 201, 0.14);
  --ps-pd-pill-border: rgba(91, 57, 201, 0.35);
}

.theme--dark .ps-product-detail {
  --ps-pd-ink: #eef0fa;
  --ps-pd-muted: #9aa1bb;
  --ps-pd-faint: #7c82a0;
  --ps-pd-accent: #9a8cf0;
  --ps-pd-accent-soft: #b3a8f5;
  --ps-pd-card-bg: #1b1d30;
  --ps-pd-card-border: rgba(154, 140, 240, 0.16);
  --ps-pd-line: rgba(148, 154, 184, 0.12);
  --ps-pd-line-strong: rgba(154, 140, 240, 0.22);
  --ps-pd-track: rgba(154, 140, 240, 0.14);
  --ps-pd-control-bg: #22243a;
  --ps-pd-danger: #f87171;
  --ps-pd-pill-bg: rgba(154, 140, 240, 0.18);
  --ps-pd-pill-border: rgba(154, 140, 240, 0.4);
}

/* ---------- Header ---------- */

.ps-pd-header {
  gap: 18px;
  margin-bottom: 14px;
}

.ps-pd-idline {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 11.5px;
  color: var(--ps-pd-muted);
  font-variant-numeric: tabular-nums;
  margin-bottom: 4px;
}

.ps-pd-title {
  margin: 0 0 7px;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--ps-pd-ink);
  overflow-wrap: anywhere;
}

.ps-pd-badges {
  gap: 7px;
}

.ps-badge {
  font-size: 11.5px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
  color: var(--ps-pd-muted);
  background: var(--ps-pd-track);
  border: 1px solid var(--ps-pd-card-border);
}

.ps-badge--on {
  color: var(--ps-pd-accent);
  background: var(--ps-pd-pill-bg);
  border-color: var(--ps-pd-pill-border);
}

.ps-pd-actions {
  gap: 9px;
  flex-wrap: wrap;
}

.ps-pd-btn {
  text-transform: none;
  font-weight: 600;
  font-size: 13px;
  border-color: var(--ps-pd-card-border) !important;
  color: var(--ps-pd-muted) !important;
  background: var(--ps-pd-control-bg) !important;
}

.ps-pd-btn--accent {
  border-color: var(--ps-pd-accent) !important;
  color: var(--ps-pd-accent) !important;
}

/* ---------- Stats ---------- */

.ps-pd-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

@media (max-width: 1100px) {
  .ps-pd-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 560px) {
  .ps-pd-stats {
    grid-template-columns: minmax(0, 1fr);
  }
}

.ps-stat-card,
.ps-price-card,
.ps-history-card {
  background: var(--ps-pd-card-bg);
  border: 1px solid var(--ps-pd-card-border);
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.ps-stat-label,
.ps-price-label,
.ps-history-label {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ps-pd-faint);
  white-space: nowrap;
}

.ps-stat-tag {
  font-size: 11px;
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
  color: var(--ps-pd-muted);
  background: var(--ps-pd-track);
  border: 1px solid var(--ps-pd-card-border);
}

.ps-stat-tag--on {
  color: var(--ps-pd-accent);
  background: var(--ps-pd-pill-bg);
  border-color: var(--ps-pd-pill-border);
}

.ps-stat-value-row,
.ps-price-value-row,
.ps-history-value-row {
  gap: 5px;
  min-width: 0;
}

.ps-stat-value {
  font-size: 21px;
  font-weight: 800;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: var(--ps-pd-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ps-stat-value--accent {
  color: var(--ps-pd-accent);
}

.ps-stat-unit,
.ps-price-unit,
.ps-history-unit {
  font-size: 12px;
  color: var(--ps-pd-muted);
}

.ps-stat-note {
  font-size: 11px;
  color: var(--ps-pd-faint);
}

/* ---------- Main grid ---------- */

.ps-pd-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 366px;
  gap: 16px;
  align-items: start;
}

@media (max-width: 960px) {
  .ps-pd-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.ps-pd-main {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.ps-pd-side {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ps-card {
  background: var(--ps-pd-card-bg);
  border: 1px solid var(--ps-pd-card-border);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
  padding: 20px 22px 22px;
}

.ps-card--side {
  padding: 18px 20px 20px;
}

.ps-section-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.ps-section-head h2 {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: var(--ps-pd-ink);
}

.ps-tick {
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--ps-pd-accent);
  display: inline-block;
  flex: 0 0 3px;
}

.ps-section-note {
  font-size: 11.5px;
  color: var(--ps-pd-faint);
}

/* ---------- Identity ---------- */

.ps-identity-grid {
  display: grid;
  grid-template-columns: 168px minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

@media (max-width: 640px) {
  .ps-identity-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.ps-identity-media {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ps-media-box {
  position: relative;
  overflow: hidden;
  height: 168px;
  border-radius: 12px;
  background: var(--ps-pd-track);
  border: 1px solid var(--ps-pd-card-border);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ps-pd-faint);
}

.ps-media-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 5px 8px;
  background: rgba(0, 0, 0, 0.55);
  font-size: 10px;
  color: #e2e8f0;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-gallery-badges {
  gap: 6px;
}

.ps-gallery-badge {
  font-size: 10.5px;
  padding: 3px 8px;
  border-radius: 999px;
  color: var(--ps-pd-faint);
  border: 1px solid var(--ps-pd-card-border);
}

.ps-gallery-badge.is-on {
  color: var(--ps-pd-accent);
  border-color: var(--ps-pd-pill-border);
}

.ps-media-note {
  font-size: 10.5px;
  color: var(--ps-pd-faint);
}

.ps-identity-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.ps-names-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ps-field-caption {
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ps-pd-muted);
}

.ps-name-row {
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  gap: 12px;
  align-items: baseline;
  padding: 7px 0;
  border-bottom: 1px solid var(--ps-pd-line);
}

.ps-name-row:last-child {
  border-bottom: none;
}

.ps-name-dot {
  width: 5px;
  height: 5px;
  border-radius: 999px;
  display: inline-block;
  margin-right: 7px;
  background: var(--ps-pd-card-border);
}

.ps-name-dot.is-on {
  background: var(--ps-pd-accent-soft);
}

.ps-name-lang {
  font-size: 11.5px;
  color: var(--ps-pd-muted);
}

.ps-name-value {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--ps-pd-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-name-value.is-muted {
  color: var(--ps-pd-faint);
  font-style: italic;
  font-weight: 400;
}

.ps-fields-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 18px;
}

.ps-meta-grid {
  display: grid;
  gap: 14px 18px;
  margin-top: 16px;
}

.ps-meta-grid--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.ps-meta-grid--4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

@media (max-width: 640px) {
  .ps-fields-grid,
  .ps-meta-grid--3,
  .ps-meta-grid--4 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.ps-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.ps-field-label {
  font-size: 11px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--ps-pd-muted);
}

.ps-field-value {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--ps-pd-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-field-note {
  font-size: 10.5px;
  color: var(--ps-pd-faint);
}

.ps-desc-block {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.ps-desc-text {
  font-size: 13.5px;
  line-height: 1.55;
  color: #334155;
}

.theme--dark .ps-desc-text {
  color: var(--ps-pd-ink);
}

.ps-desc-text.is-empty {
  color: var(--ps-pd-faint);
  font-style: italic;
}

/* ---------- Price ---------- */

.ps-price-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

@media (max-width: 640px) {
  .ps-price-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.ps-price-card {
  background: var(--ps-pd-track);
  border: 1px solid var(--ps-pd-line-strong);
}

.ps-price-card.is-tint {
  background: var(--ps-pd-pill-bg);
  border-color: var(--ps-pd-pill-border);
}

.ps-price-value {
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: var(--ps-pd-ink);
  white-space: nowrap;
}

.ps-price-value.is-tint {
  color: var(--ps-pd-accent);
}

.ps-price-note {
  font-size: 11px;
  color: var(--ps-pd-faint);
}

/* ---------- Inventory ---------- */

.ps-stock-value-row {
  gap: 10px;
  margin-bottom: 10px;
}

.ps-stock-value {
  font-size: 23px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--ps-pd-ink);
}

.ps-stock-unit {
  font-size: 12.5px;
  color: var(--ps-pd-muted);
}

.ps-stock-notify-note {
  font-size: 11.5px;
  color: var(--ps-pd-accent);
}

.ps-stock-bar {
  position: relative;
  height: 6px;
  border-radius: 999px;
  background: var(--ps-pd-track);
  overflow: hidden;
}

.ps-stock-bar__fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-radius: 999px;
  background: linear-gradient(to right, var(--ps-pd-accent-soft), var(--ps-pd-accent));
}

.ps-stock-bar__fill.is-warn {
  background: var(--ps-pd-accent-soft);
}

/* ---------- Sales history ---------- */

.ps-history-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

@media (max-width: 640px) {
  .ps-history-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.ps-history-card {
  background: var(--ps-pd-track);
  border: 1px solid var(--ps-pd-line-strong);
}

.ps-history-value {
  font-size: 18px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--ps-pd-ink);
}

.ps-history-note {
  margin-top: 12px;
  font-size: 11.5px;
  color: var(--ps-pd-muted);
}

/* ---------- Tables (variants / bundle) ---------- */

.ps-table-wrap {
  overflow-x: auto;
}

.ps-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

.ps-table th {
  text-align: left;
  font-size: 10.5px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--ps-pd-faint);
  padding: 0 8px 8px;
  border-bottom: 1px solid var(--ps-pd-line-strong);
  white-space: nowrap;
}

.ps-table td {
  padding: 9px 8px;
  border-bottom: 1px solid var(--ps-pd-line);
  color: var(--ps-pd-ink);
}

.ps-table tr:last-child td {
  border-bottom: none;
}

.ps-th--right,
.ps-td--right {
  text-align: right;
}

.ps-td-strong {
  font-weight: 700;
}

.ps-td-mono {
  font-variant-numeric: tabular-nums;
  color: var(--ps-pd-muted);
}

.ps-td--right.is-danger {
  color: var(--ps-pd-danger);
}

.ps-mini-chip {
  display: inline-flex;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid var(--ps-pd-card-border);
  color: var(--ps-pd-muted);
  margin-right: 4px;
}

.ps-bundle-avatar {
  background: var(--ps-pd-track) !important;
}

/* ---------- Sidebar ---------- */

.ps-toggle-row {
  display: flex;
  align-items: flex-start;
  gap: 11px;
}

.ps-toggle-row + .ps-toggle-row {
  margin-top: 13px;
}

.ps-toggle-meta {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}

.ps-toggle-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--ps-pd-ink);
}

.ps-toggle-title.is-muted {
  color: var(--ps-pd-faint);
}

.ps-toggle-note {
  font-size: 11.5px;
  color: var(--ps-pd-muted);
}

.ps-toggle-note--italic {
  font-style: italic;
  color: var(--ps-pd-faint);
}

.ps-switch ::v-deep .v-input--selection-controls__input {
  transform: scale(0.85);
}

.ps-side-row {
  display: grid;
  grid-template-columns: 104px minmax(0, 1fr);
  gap: 12px;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: 1px solid var(--ps-pd-line);
}

.ps-side-row:last-child {
  border-bottom: none;
}

.ps-side-row--tight {
  grid-template-columns: 104px minmax(0, 1fr);
  padding: 7px 0;
}

.ps-side-label {
  font-size: 11.5px;
  color: var(--ps-pd-muted);
}

.ps-side-value-col {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.ps-side-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--ps-pd-ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-side-value--mono {
  font-variant-numeric: tabular-nums;
}

.ps-cap-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-top: 1px solid var(--ps-pd-line);
}

.ps-cap-row:first-child {
  border-top: none;
}

.ps-cap-mark {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ps-pd-faint);
  border: 1px solid var(--ps-pd-card-border);
}

.ps-cap-mark.is-on {
  color: #ffffff;
  background: var(--ps-pd-accent);
  border-color: var(--ps-pd-accent);
}
</style>
